import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateToolboxTopicPlan } from "../../../api/mutations";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import { useNotification } from "../../../hooks/useNotification";
import TimeframeFields, { TimeframeMode } from "./components/TimeframeFields";

const cancelLinkClass =
  "inline-flex items-center justify-center font-medium h-10 text-sm rounded-md transition-all duration-150 ease-in-out whitespace-pre-line bg-transparent text-electrum hover:text-electrum-300 px-4";

export default function NewPage() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [form, setForm] = useState({ goal: "", cue: "", timeframe: "" });
  const [mode, setMode] = useState<TimeframeMode>("cue");

  const mutation = useCreateToolboxTopicPlan({
    onSuccess: async () => {
      notify("success", "Plan created");
      await queryClient.invalidateQueries({
        queryKey: ["toolboxTopicPlans", topicId],
      });
      navigate(`/toolboxes/${topicId}`);
    },
    onError: () =>
      notify("error", "We couldn't save your plan. Please try again."),
  });

  if (!topicId) {
    return <Navigate to="/error" replace />;
  }

  const backTo = `/toolboxes/${topicId}`;

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6 pt-4">
      <Link
        to={backTo}
        className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors w-fit"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back
      </Link>
      <h1 className="text-2xl font-bold text-gray-800">Create plan</h1>

      <div>
        <label
          htmlFor="plan-goal"
          className="block text-sm font-medium text-gray-400 mb-2"
        >
          Goal: To... (optional)
        </label>
        <Input
          id="plan-goal"
          value={form.goal}
          onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
        />
      </div>

      <TimeframeFields
        mode={mode}
        onModeChange={setMode}
        cue={form.cue}
        onCueChange={(cue) => setForm((f) => ({ ...f, cue }))}
        timeframe={form.timeframe}
        onTimeframeChange={(timeframe) => setForm((f) => ({ ...f, timeframe }))}
      />

      <div>
        <p className="text-sm font-medium text-gray-400 mb-1">Tools</p>
        <p className="text-sm text-gray-400 italic">
          Tools will be added here.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() =>
            mutation.mutate({
              topicId,
              body: form,
            })
          }
          loading={mutation.isPending}
          disabled={mutation.isPending}
        >
          Save
        </Button>
        <Link to={backTo} className={cancelLinkClass}>
          Cancel
        </Link>
      </div>
    </div>
  );
}
